import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Truck, Plus, Loader2 } from 'lucide-react';
import { fetchLowStockSummaryEnhanced } from '@/app/services/reportService';
import { LowStockReportParams, LowStockItemEnhanced } from '@/app/types/lowStockReport';
import { LowStockDataTable } from './LowStockDataTable';
import { LowStockFilters } from './LowStockFilters';
import { toast } from 'sonner';

const LowStockReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<LowStockItemEnhanced[]>([]);
  const [filters, setFilters] = useState<LowStockReportParams>({
    skip: 0,
    limit: 50,
    sort_by: 'shortage',
    sort_dir: 'desc'
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['low-stock-report-enhanced', filters],
    queryFn: () => fetchLowStockSummaryEnhanced(filters),
    keepPreviousData: true,
  });

  const handleFilterChange = (newFilters: Partial<LowStockReportParams>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      skip: 0 // Reset pagination when filters change
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({
      ...prev,
      skip: (page - 1) * (prev.limit || 50)
    }));
  };

  const handleCreateInternalTransfer = () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to transfer');
      return;
    }

    // Group selected items by suggested source godown
    const transferItems = selectedItems.map(item => {
      // Find the best source site (with excess stock)
      const sourceSite = item.site_stock_details.find(site => 
        site.excess_quantity && parseFloat(site.excess_quantity) > 0
      );

      // Calculate transfer quantity - either the full shortage or available excess
      const transferQuantity = sourceSite 
        ? Math.min(
            parseFloat(item.shortage || '0'),
            parseFloat(sourceSite.excess_quantity || '0')
          )
        : parseFloat(item.shortage || '0');

      return {
        item_id: item.item_id,
        item: {
          id: item.item_id,
          name: item.item_name,
          sku: item.item_code,
          unit: { 
            id: 1, // Default unit ID
            name: item.unit_name,
            symbol: item.unit_name // Using name as symbol for now
          }
        },
        quantity: transferQuantity,
        rate: 0, // Will be set in the form
        unit_id: 1, // Default unit, will be updated if needed
        suggested_source_site: sourceSite?.site_name,
        suggested_source_site_id: sourceSite?.site_id,
        destination_godown_id: item.godown_id,
        destination_godown_name: item.godown_name,
        destination_site_name: item.current_site_name,
        notes: `Low stock transfer from ${sourceSite?.site_name || 'TBD'} - ${item.suggested_action}`
      };
    });

    // Navigate to internal transfer page with pre-filled data
    navigate('/items/internal-transfers/detail', {
      state: {
        fromLowStockReport: true,
        prefilledItems: transferItems,
        sourceContext: 'low-stock-report'
      }
    });

    toast.info(`Creating internal transfer for ${selectedItems.length} item(s)`);
  };

  const handleCreatePurchaseOrder = () => {
    const itemsForPurchase = selectedItems.filter(
      item => item.suggested_action === 'purchase_required'
    );

    if (itemsForPurchase.length === 0) {
      toast.warning('No items require purchase orders. Items with available stock for transfer should use Internal Transfer.');
      return;
    }

    // This would navigate to purchase order creation
    // For now, just show a message
    toast.info('Purchase order creation will be implemented in the next phase');
  };

  const getActionButton = () => {
    if (selectedItems.length === 0) return null;

    const hasTransferableItems = selectedItems.some(
      item => item.suggested_action !== 'purchase_required'
    );
    
    const hasPurchaseItems = selectedItems.some(
      item => item.suggested_action === 'purchase_required'
    );

    return (
      <div className="flex gap-2">
        {hasTransferableItems && (
          <Button onClick={handleCreateInternalTransfer} variant="default">
            <Truck className="h-4 w-4 mr-2" />
            Create Internal Transfer ({selectedItems.filter(i => i.suggested_action !== 'purchase_required').length})
          </Button>
        )}
        {hasPurchaseItems && (
          <Button onClick={handleCreatePurchaseOrder} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Create Purchase Order ({selectedItems.filter(i => i.suggested_action === 'purchase_required').length})
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 text-warning" />
          <h1 className="text-xl font-semibold tracking-tight">Low Stock Report</h1>
        </div>
        {getActionButton()}
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Low Stock Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-semibold tracking-tight">{data.total}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Transfer Available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-semibold tracking-tight text-success">
                {data.items.filter(i => i.suggested_action === 'transfer_available').length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Partial Transfer
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-semibold tracking-tight text-warning">
                {data.items.filter(i => i.suggested_action === 'partial_transfer').length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Purchase Required
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xl font-semibold tracking-tight text-destructive">
                {data.items.filter(i => i.suggested_action === 'purchase_required').length}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <LowStockFilters 
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* Data Table */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <Card>
          <CardContent className="py-10">
            <div className="text-center text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <p>Error loading low stock report</p>
              <Button onClick={() => refetch()} className="mt-4" variant="outline">
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : data && data.items.length > 0 ? (
        <LowStockDataTable
          data={data.items}
          total={data.total}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          currentPage={Math.floor((filters.skip || 0) / (filters.limit || 50)) + 1}
          pageSize={filters.limit || 50}
          onPageChange={handlePageChange}
          onSort={(column, direction) => {
            setFilters(prev => ({
              ...prev,
              sort_by: column as any,
              sort_dir: direction
            }));
          }}
        />
      ) : (
        <Card>
          <CardContent className="py-10">
            <div className="text-center text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-success" />
              <p className="text-lg font-medium">No low stock items found</p>
              <p className="text-sm mt-2">All items are adequately stocked</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LowStockReportPage;